import React from 'react';

import { Checkbox, type CheckboxChangeEvent, Col, Flex, Row } from 'antd-v5';
import cn from 'classnames';
import startCase from 'lodash/startCase';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Text } from 'scouting-ui/lib';

import { RenewalOrderStatus } from '@appTypes/enums';
import { programSel } from '@context/duck/selectorsTyped';
import {
  peopleFilters,
  programGrownUpAges,
  registrationSituationStatuses,
  renewalStatuses,
} from '@modules/advancement/packRoster/constants';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { ConditionalRender } from '@shared/components';
import { Program } from '@shared/constants';
import { intl } from '@shared/localization';

import styles from '../PackRosterFilterCard.less';

interface Props {
  values: Record<string, boolean>;
  onChange: (filter: Record<string, boolean>) => void;
}
const FilterSection: React.FC<Props> = ({ values, onChange }) => {
  const program = useSelector(programSel);

  const handleChange = (e: CheckboxChangeEvent) => {
    const { name } = e.target;
    const newValues = {
      ...values,
      [name!]: !values[name!],
    };

    onChange(newValues);
  };

  const renderCheckbox = (name: string, label: string) => (
    <Checkbox
      key={name}
      name={name}
      checked={values[name]}
      onChange={handleChange}
    >
      <Text strong>{label}</Text>
    </Checkbox>
  );

  return (
    <>
      <div
        className={cn(styles.filtersContainer, styles.rosterFiltersContainer)}
      >
        <Row gutter={[16, 16]}>
          <Col>
            {/* Type Filters */}
            <Flex vertical gap={'small'}>
              <Text
                color="color-primary-oldScoutingBlue"
                strong
                font="body-body-3"
              >
                <FormattedMessage id="packRoster.PackRosterFilterCard.type" />
              </Text>
              {renderCheckbox(
                peopleFilters.YOUTH,
                intl.formatMessage({
                  id: 'advancement.PackRoster.showYouth',
                }),
              )}
              {program !== Program.CUB_SCOUT &&
                renderCheckbox(
                  peopleFilters.AGED_YOUTH,
                  intl.formatMessage(
                    {
                      id: 'advancement.PackRoster.showAgedYouth',
                    },
                    {
                      age: programGrownUpAges[program],
                    },
                  ),
                )}
              {renderCheckbox(
                peopleFilters.ADULTS,
                intl.formatMessage({
                  id: 'advancement.PackRoster.showAdults',
                }),
              )}
              {program === Program.CUB_SCOUT &&
                renderCheckbox(
                  peopleFilters.DEN_CHIEFS,

                  intl.formatMessage({
                    id: 'advancement.PackRoster.showDenChiefs',
                  }),
                )}
            </Flex>
          </Col>
          <Col>
            {/* Renewal Status Filters */}
            <Flex vertical gap={'small'} key="renewalStatus">
              <Text
                color="color-primary-oldScoutingBlue"
                strong
                font="body-body-3"
              >
                <FormattedMessage id="packRoster.PackRosterFilterCard.renewalStatus" />
              </Text>
              {Object.values(renewalStatuses).map(status =>
                renderCheckbox(status, status),
              )}
            </Flex>
          </Col>
          <Col>
            {/* Registration Status Filters */}
            <Flex gap="small" vertical>
              <Text
                color="color-primary-oldScoutingBlue"
                strong
                font="body-body-3"
              >
                <FormattedMessage id="packRoster.PackRosterFilterCard.registrationStatus" />
              </Text>
              {Object.values(registrationSituationStatuses).map(status =>
                renderCheckbox(
                  status,
                  intl.formatMessage({
                    id: `packRoster.PackRosterFilterCard.${status}`,
                  }),
                ),
              )}
            </Flex>
          </Col>
          <Col>
            {/* Order Status */}
            <ConditionalRender
              value={featureFlags.getFlag('SBL_5483_GROUPBY_ORDER_STATUS')}
            >
              <Flex key="orderStatus" gap="small" vertical>
                <Text color="color-primary-oldScoutingBlue" strong>
                  <FormattedMessage id="packRoster.PackRosterFilterCard.orderStatus" />
                </Text>
                {Object.entries(RenewalOrderStatus).map(([key, label]) =>
                  renderCheckbox(`order_status_${key}`, startCase(label)),
                )}
              </Flex>
            </ConditionalRender>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default FilterSection;
